import React from 'react'
import { Col, Form, Input, Row, Select } from 'antd'
import AddressSelect from 'components/Address/Select'
import type { TypeAddressSelectProps } from 'components/Address/Select'
import useAddressData from 'components/Address/useAddressData'
import { onFormItemDiff } from 'components/Address/utils'

const FIELD_NAMES = {
  street: 'street',
  city: 'city',
  state: 'state',
  county: 'county',
  zip: 'zip',
} as const

/**
 * Modal-friendly office address fields.
 * Reuses Google address select + state/county data from the shared Address module.
 */
const ModalOfficeAddress: React.FC = () => {
  const address = useAddressData()
  const form = Form.useFormInstance()

  const onRelation = (addr: Parameters<NonNullable<TypeAddressSelectProps['onAddress']>>[0]) => {
    form.setFields([
      { name: FIELD_NAMES.zip, value: addr?.zip },
      { name: FIELD_NAMES.city, value: addr?.city },
      { name: FIELD_NAMES.state, value: addr?.state },
      { name: FIELD_NAMES.county, value: addr?.county },
    ])
    form.validateFields(Object.values(FIELD_NAMES))
  }

  const onStateChange = () => {
    form.setFields([{ name: FIELD_NAMES.county, value: undefined }])
  }

  return (
    <div className="um-new-org__address">
      <Form.Item
        label="Office address"
        name={FIELD_NAMES.street}
        className="um-new-org__field um-new-org__field--street"
        rules={[{ required: true, message: 'Office address is required' }]}
      >
        <AddressSelect
          fullAddress
          onAddress={onRelation}
          placeholder="Enter street address"
        />
      </Form.Item>

      <Row gutter={12} className="um-new-org__address-row">
        <Col span={6}>
          <Form.Item
            label="State"
            name={FIELD_NAMES.state}
            className="um-new-org__field"
            rules={[{ required: true, message: 'State is required' }]}
          >
            <Select
              size="large"
              showSearch
              placeholder="State"
              optionFilterProp="label"
              onChange={onStateChange}
              loading={address?.loading}
              options={address?.data?.state}
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            noStyle
            shouldUpdate={(p, c) => onFormItemDiff(p, c, FIELD_NAMES.state)}
          >
            {(innerForm) => {
              const state = innerForm.getFieldValue(FIELD_NAMES.state)
              return (
                <Form.Item
                  label="County"
                  name={FIELD_NAMES.county}
                  className="um-new-org__field"
                  rules={[{ required: true, message: 'County is required' }]}
                >
                  <Select
                    size="large"
                    showSearch
                    placeholder="County"
                    optionFilterProp="label"
                    loading={address?.loading}
                    options={address?.data?.county?.[state]}
                  />
                </Form.Item>
              )
            }}
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            label="City"
            name={FIELD_NAMES.city}
            className="um-new-org__field"
            rules={[{ required: true, message: 'City is required' }]}
          >
            <Input size="large" placeholder="City" />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            label="Zip"
            name={FIELD_NAMES.zip}
            className="um-new-org__field"
            rules={[{ required: true, message: 'Zip is required' }]}
          >
            <Input size="large" placeholder="Zip" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  )
}

export default ModalOfficeAddress
